import React, { ReactNode, useState, useEffect } from "react";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSortUp,
  faSortDown,
  faAngleRight,
  faAngleLeft,
  faFilter,
  faArrowUp,
  faArrowDown,
  faDownload
} from "@fortawesome/free-solid-svg-icons";

import "./DataTable.css";
import Loader from "components/Loader";
import NoData from "assets/NodataImg.jpg";
import PillButton from "components/button/Pills";
import CustomSearch from "components/searchbar/CustomSearch";
import { UI_CONFIG } from "config/uiConfig";
import FilterButton from "components/button/FilterButton";
import CustomButton from "components/button/CustomButton";

export interface Column {
  bold?: boolean;
  key: string;
  label: string;
  actions?: (row: Row) => React.ReactNode;
  excludeExport?: boolean;
  exportLabel?: string; // override header label in Excel
  formatter?: (value: any, row: Row) => string | number; // format cell value for Excel export
  type?: string;
  maxWidth?: string;
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  minWidth?: string;
  render?: (value: ReactNode, row: Row) => React.ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right';
  trendIndicator?: boolean; // Show trend indicator for this column
}

export interface Row {
  id: number;
  [key: string]: ReactNode;
}

export interface DataTableProps {
  data: Row[];
  columns: Column[];
  title?: string;
  onAddBtnClick?: () => void;
  loading?: boolean;
  isAddBtnTrue?: boolean;
  isSearchBtn?: boolean;
  isExport?: boolean;
  position?: string;
  exportPosition?: string;
  density?: 'compact' | 'normal' | 'comfortable';
  stickyHeader?: boolean;
  showTrending?: boolean;
  onRowClick?: (row: Row) => void;
  emptyStateMessage?: string;
  defaultSortColumn?: string;
  defaultSortDirection?: 'asc' | 'desc';
  selectedRows?: number[];
  onSelectRows?: (rows: number[]) => void;
  showColumnFilters?: boolean;
  serverSide?: boolean;
  currentPage?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  onSearchChange?: (value: string) => void;
  isInsertExcel?: boolean;
  handleInsertClick?: () => void
}

const computeTrend = (
  showTrending: boolean,
  column: Column,
  prevValue: ReactNode,
  currentValue: ReactNode
): 'up' | 'down' | null => {
  if (!showTrending || !column.trendIndicator) return null;
  if (prevValue === null || currentValue === null) return null;
  if (typeof currentValue !== 'number' || typeof prevValue !== 'number') return null;
  if (currentValue > prevValue) return 'up';
  if (currentValue < prevValue) return 'down';
  return null;
};

const getDensityClass = (density: string): string => {
  if (density === 'compact') return 'py-1 px-2';
  if (density === 'comfortable') return 'py-4 px-6';
  return 'py-2 px-4';
};

interface CellContentProps {
  column: Column;
  row: Row;
  rowIndex: number;
  activePage: number;
  itemsPerPage: number;
  showTrending: boolean;
  trend: 'up' | 'down' | null;
}

const CellContent: React.FC<CellContentProps> = ({
  column, row, rowIndex, activePage, itemsPerPage, showTrending, trend
}) => {
  if (column.key === "actions") return <>{column.actions?.(row)}</>;
  if (column.render) return <>{column.render(row[column.key], row)}</>;
  if (column.key === "id") {
    return <strong>#{(activePage - 1) * itemsPerPage + rowIndex + 1}</strong>;
  }
  return (
    <div className="flex items-center min-w-0 w-full">
      <span
        // className="block truncate w-full min-w-0 max-w-full overflow-hidden"
        className="block truncate max-w-full"
        title={row[column.key] ? String(row[column.key]) : ""}
      >
        {row[column.key]}
      </span>
      {showTrending && column.trendIndicator && trend && (
        <span className={`ml-2 ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
          <FontAwesomeIcon icon={trend === 'up' ? faArrowUp : faArrowDown} size="xs" />
        </span>
      )}
    </div>
  );
};

const DataTable: React.FC<DataTableProps> = ({
  data,
  columns,
  title = "",
  onAddBtnClick,
  loading = false,
  isAddBtnTrue = true,
  isSearchBtn = true,
  isExport = true,
  position = "flex flex-row space-y-2 justify-between mb-4 w-full",
  exportPosition = "w-28 text-gray-500 mr-3 font-medium",
  density = "normal",
  stickyHeader = true,
  showTrending = false,
  onRowClick,
  emptyStateMessage = "No Data Found",
  defaultSortColumn,
  defaultSortDirection = "asc",
  showColumnFilters = false,
  serverSide = false,
  currentPage: serverCurrentPage = 1,
  totalItems: serverTotalItems = 0,
  itemsPerPage: serverItemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  onSearchChange,
  isInsertExcel = false,
  handleInsertClick
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [paginatedData, setPaginatedData] = useState<Row[]>([]);
  const [filteredData, setFilteredData] = useState<Row[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState<number>(serverSide ? serverItemsPerPage : 10);
  const [sortColumn, setSortColumn] = useState<string | null>(defaultSortColumn || null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(defaultSortDirection);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [isColumnFiltersVisible, setIsColumnFiltersVisible] = useState<boolean>(showColumnFilters);

  // Track previous values for trending indicators
  const [previousData, setPreviousData] = useState<Row[]>([]);

  // Sync itemsPerPage with server-side value when in serverSide mode
  useEffect(() => {
    if (serverSide) {
      setItemsPerPage(serverItemsPerPage);
    }
  }, [serverSide, serverItemsPerPage]);

  // Clean up console logs
  // Track data changes for trending features
  useEffect(() => {
    if (data && data.length > 0) {
      // Store current data as previous for trending indicators
      setPreviousData([...data]);
    }
  }, [data]);

  useEffect(() => {
    setSearchTerm("");
  }, [data]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Apply filters and sorting
  useEffect(() => {
    if (serverSide) {
      // In server-side mode, just use the data directly
      setPaginatedData(data);
      const totalPagesCalc = Math.ceil(serverTotalItems / itemsPerPage);
      setTotalPages(totalPagesCalc);
      return;
    }

    if (!Array.isArray(data)) return; // Ensure data is an array before processing
    let updatedData = [...data];

    // Apply search filter
    if (searchTerm) {
      updatedData = updatedData.filter((row) =>
        Object.values(row).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply column filters if enabled
    if (showColumnFilters && Object.keys(columnFilters).length > 0) {
      updatedData = updatedData.filter(row => {
        return Object.entries(columnFilters).every(([key, filterValue]) => {
          if (!filterValue) return true;
          const cellValue = row[key];
          return cellValue &&
            cellValue.toString().toLowerCase().includes(filterValue.toLowerCase());
        });
      });
    }

    // Apply sorting from latest visit
    if (sortColumn) {
      updatedData.sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];

        // Give Latest Visit at based on createDate
        if (sortColumn === "createdDate") {
          const parseDate = (val: any) => {
            if (!val) return 0;

            const parts = val.toString().split("/");
            if (parts.length === 3) {
              const [day, month, year] = parts;
              return new Date(`${year}-${month}-${day}`).getTime();
            }

            return new Date(val).getTime();
          };

          const aDate = parseDate(aValue);
          const bDate = parseDate(bValue);

          return bDate - aDate; // latest first
        }

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;

        return 0;
      });
    }

    setFilteredData(updatedData);
  }, [searchTerm, sortColumn, sortDirection, data, columnFilters, showColumnFilters, serverSide, serverTotalItems, itemsPerPage]);

  // Pagination
  useEffect(() => {
    if (serverSide) return; // Skip client-side pagination in server-side mode

    if (!filteredData || filteredData.length === 0) return;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedData(filteredData.slice(startIndex, endIndex));
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    setTotalPages(totalPages);
  }, [currentPage, itemsPerPage, filteredData, serverSide]);

  // Helper methods to reduce cognitive complexity
  const getCurrentPage = () => serverSide ? serverCurrentPage : currentPage;

  // Separate handlers for client-side pagination
  const handleClientPrevPage = () => {
    setCurrentPage((prevPage) => Math.max(1, prevPage - 1));
  };

  const handleClientNextPage = () => {
    setCurrentPage((prevPage) => Math.min(totalPages, prevPage + 1));
  };

  const handleClientPageClick = (page: number) => {
    setCurrentPage(page);
  };

  // Separate handlers for server-side pagination
  const handleServerPrevPage = () => {
    if (onPageChange) {
      onPageChange(serverCurrentPage - 1);
    }
  };

  const handleServerNextPage = () => {
    if (onPageChange) {
      onPageChange(serverCurrentPage + 1);
    }
  };

  const handleServerPageClick = (page: number) => {
    if (onPageChange) {
      onPageChange(page);
    }
  };

  // Composed handlers that delegate to appropriate method
  const handlePrevPage = serverSide ? handleServerPrevPage : handleClientPrevPage;
  const handleNextPage = serverSide ? handleServerNextPage : handleClientNextPage;
  const handlePageClick = serverSide ? handleServerPageClick : handleClientPageClick;

  // Generate page numbers for pagination
  // const generatePageNumbers = () => {
  //   const pages = [];
  //   const maxPagesToShow = 5;

  //   if (totalPages <= maxPagesToShow) {
  //     // Show all pages if total pages are less than max pages to show
  //     for (let i = 1; i <= totalPages; i++) {
  //       pages.push(i);
  //     }
  //   } else {
  //     // Always show first page
  //     pages.push(1);

  //     // Calculate start and end of middle pages

  //     const activePage = getCurrentPage();

  //     let startPage = Math.max(2, activePage - 1);
  //     let endPage = Math.min(totalPages - 1, activePage + 1);

  //     if (activePage <= 2) {
  //       endPage = Math.min(totalPages - 1, 4);
  //     } else if (activePage >= totalPages - 1) {
  //       startPage = Math.max(2, totalPages - 3);
  //     }
  //     //   endPage = Math.min(totalPages - 1, 4);
  //     // } else if (currentPage >= totalPages - 1) {
  //     //   startPage = Math.max(2, totalPages - 3);
  //     // }

  //     // Add ellipsis if needed
  //     if (startPage > 2) {
  //       pages.push(-1); // Use -1 to represent ellipsis
  //     }

  //     // Add middle pages
  //     for (let i = startPage; i <= endPage; i++) {
  //       pages.push(i);
  //     }

  //     // Add ellipsis if needed
  //     if (endPage < totalPages - 1) {
  //       pages.push(-2); // Use -2 to represent ellipsis
  //     }

  //     // Always show last page
  //     pages.push(totalPages);
  //   }

  //   return pages;
  // };
  const generatePageNumbers = () => {
    const pages: number[] = [];
    const activePage = getCurrentPage();
    const delta = 1; // how many pages around current

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= activePage - delta && i <= activePage + delta)
      ) {
        pages.push(i);
      } else if (
        i === activePage - delta - 1 ||
        i === activePage + delta + 1
      ) {
        pages.push(-1); // ellipsis
      }
    }

    return pages;
  };
  const totalPagesSafe = serverSide
    ? Math.ceil(serverTotalItems / itemsPerPage)
    : totalPages;

  const handleExportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");
    //  Removed action columns marked for exclusion
    const exportColumns = columns.filter(
      (column) => !column.excludeExport
    );

    // const exportColumns = columns.filter((column) => !column.excludeExport);
    // Use exportColumns here
    const columnsLabel = exportColumns.map((column) =>
      column.exportLabel ??
      column.label
        .replace(/[↑↓]/g, '')         // strip sort indicators
        .trim()
    );
    const rows = data.map((data1) =>
      exportColumns.map((col) => {
        let value = col.formatter ? col.formatter(data1[col.key], data1) : data1[col.key];
        return value === undefined || value === null ? "" : value;
      })
    );
    worksheet.addRow(columnsLabel);
    rows.forEach((row) => worksheet.addRow(row));
    const blob = await workbook.xlsx.writeBuffer();
    const today = new Date().toISOString().split("T")[0];
    const fileName = title ? `${title}_${today}.xlsx` : `exported_data_${today}.xlsx`;
    saveAs(
      new Blob([blob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      fileName
    );
  };

  const handleAddClickEvent = () => {
    if (onAddBtnClick) onAddBtnClick();
  };

  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newItemsPerPage = parseInt(e.target.value);
    if (serverSide && onItemsPerPageChange) {
      onItemsPerPageChange(newItemsPerPage);
    } else {
      setItemsPerPage(newItemsPerPage);
    }
  };

  const handleSort = (key: string) => {
    if (sortColumn === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(key);
      setSortDirection("asc");
    }
  };

  const renderSortIndicator = (columnKey: string) => {
    if (sortColumn === columnKey) {
      return sortDirection === "asc" ? (
        <div className="center" style={{ marginBottom: -5, marginLeft: 5 }}>
          <FontAwesomeIcon icon={faSortUp} />
        </div>
      ) : (
        <div className="center" style={{ marginTop: -5, marginLeft: 5 }}>
          <FontAwesomeIcon icon={faSortDown} />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {isSearchBtn && (
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="w-full sm:w-auto">
              <h2 className="text-gray-800 dark:text-white text-xl font-semibold">
                {title}
              </h2>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
              {isSearchBtn && (
                <div className="w-full sm:w-auto mt-1">
                  <CustomSearch

                    onChange={(value) => {
                      setSearchTerm(value);
                      onSearchChange?.(value);
                    }}
                    placeholder="Search"
                    width="100%"

                  />
                </div>
              )}

              <div className="flex items-center gap-3 w-full sm:w-auto mt-1">
                {showColumnFilters && (
                  <button
                    onClick={() => setIsColumnFiltersVisible(prev => !prev)}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded flex items-center"
                  >
                    <FontAwesomeIcon icon={faFilter} className="mr-2" />
                    Filters
                  </button>
                )}

                {isAddBtnTrue && (

                  <FilterButton handleClick={handleAddClickEvent} title="Add"></FilterButton>
                )}
                {
                  isInsertExcel && (
                    <PillButton
                      type="button"
                      className="w-full sm:w-28"
                      onClick={handleInsertClick}
                      variant="secondary"
                      size="sm"
                    >Insert </PillButton>
                  )
                }

                {isExport && (
                  // <PillButton
                  //   type="button"
                  //   className="w-full sm:w-28 text-gray-500 font-medium"
                  //   variant="primary"
                  //   onClick={handleExportToExcel}
                  //   size="sm"
                  // >
                  //   {/* <FontAwesomeIcon icon={faFileExport} className="mr-2" /> */}
                  //   Export
                  // </PillButton>
                  <CustomButton
                    onClick={handleExportToExcel}
                  // loading={isDownloading}
                  >
                    {
                      <FontAwesomeIcon icon={faDownload} className="mr-1.5" />
                    }
                    Download
                  </CustomButton>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Fixed-height card: header sticky, body scrolls, pagination pinned at bottom */}
      <div className={`flex flex-col h-[82vh] ${UI_CONFIG.table.container}`}>

        {/* ── Vertical scroll only — keeps sticky header working ── */}
        <div className="relative flex-1 overflow-y-auto min-h-0">

          {/* Loading overlay */}
          {loading && (
            <div className="sticky inset-0 z-20 flex items-center justify-center bg-white/70 dark:bg-gray-800/70"
              style={{ height: "100%", top: 0 }}>
              <Loader />
            </div>
          )}

          {!loading && paginatedData && paginatedData.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-16 text-center">
              <img src={NoData} alt="No data" className="w-40 opacity-70 mb-4" />
              <p className="text-gray-500 text-sm">{emptyStateMessage}</p>
            </div>
          ) : (
            /* Horizontal scroll on its own wrapper so its scrollbar
               sits BELOW the last row, never overlapping it */
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto border-collapse">

                {/* TABLE HEADER — sticks to the top of the y-scroll container */}
                <thead
                  className={`${UI_CONFIG.table.header} ${stickyHeader ? "sticky top-0 z-10" : ""}`}
                >
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        style={{
                          width: column.width,
                          minWidth: column.minWidth,
                          maxWidth: column.maxWidth
                        }}
                        className={`${UI_CONFIG.table.headerCell} cursor-pointer uppercase tracking-wider ${getDensityClass(density)}`}
                        onClick={() =>
                          column.sortable ? handleSort(column.key) : undefined
                        }
                      >
                        <div className="flex items-center">
                          {column.label}
                          {column.sortable && renderSortIndicator(column.key)}
                        </div>
                      </th>
                    ))}
                  </tr>

                  {/* COLUMN FILTERS */}
                  {showColumnFilters && isColumnFiltersVisible && (
                    <tr className="bg-white">
                      {columns.map((column) => (
                        <th key={column.key} className="px-2 py-2">
                          {column.filterable && (
                            <input
                              type="text"
                              placeholder={`Filter ${column.label}`}
                              value={columnFilters[column.key] || ""}
                              onChange={(e) =>
                                setColumnFilters((prev) => ({
                                  ...prev,
                                  [column.key]: e.target.value
                                }))
                              }
                              className={UI_CONFIG.input.search}
                            />
                          )}
                        </th>
                      ))}
                    </tr>
                  )}
                </thead>

                {/* TABLE BODY */}
                {/* <tbody className="bg-white divide-y divide-table-border"> */}
                <tbody className="bg-white dark:bg-table-bgDark divide-y divide-table-border dark:divide-table-borderDark">
                  {paginatedData.map((row, rowIndex) => (
                    <tr
                      key={row.id}
                      className={`${UI_CONFIG.table.row} transition ${onRowClick ? "cursor-pointer" : ""}`}
                      onClick={() => onRowClick?.(row)}
                    >
                      {columns.map((column) => {
                        const prevValue =
                          previousData[rowIndex]?.[column.key] ?? null;

                        const trend = computeTrend(
                          showTrending,
                          column,
                          prevValue,
                          row[column.key]
                        );

                        return (
                          <td
                            key={column.key}
                            className={`${UI_CONFIG.table.cell} ${getDensityClass(density)} whitespace-nowrap ${column.align === "center"
                              ? "text-center"
                              : column.align === "right"
                                ? "text-right"
                                : "text-left"
                              } ${column.className || ""}`}
                          >
                            <CellContent
                              column={column}
                              row={row}
                              rowIndex={rowIndex}
                              activePage={getCurrentPage()}
                              itemsPerPage={itemsPerPage}
                              showTrending={showTrending}
                              trend={trend}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Pagination — always pinned at bottom, never scrolls ── */}
        {/* {paginatedData && paginatedData.length > 0 && ( */}
        {/* <div className="flex-shrink-0 flex flex-row justify-between items-center px-4 py-1 border-t bg-white rounded-b-table" style={{ minHeight: 0 }}> */}
        {/* <div className="flex-shrink-0 flex flex-row justify-between items-center px-4 py-2 border-t border-table-border dark:border-table-borderDark bg-white dark:bg-table-bgDark dark:color-white rounded-b-table"> */}
        {/* ITEMS PER PAGE */}
        {/* <div className="flex flex-row items-center gap-2 text-sm text-gray-600"> */}
        {/* <div className="flex flex-row items-center gap-2 text-sm text-gray-600 dark:text-gray-300"> */}
        {/* <div className="flex flex-row border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded px-1.5 text-sm appearance-auto">
            <span className="whitespace-nowrap">Rows per page:</span>
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded px-2 py-1 text-sm appearance-auto">
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div> */}
        <div className="
          flex-shrink-0
          flex items-center justify-between
          px-4 py-3
          border-t border-table-border dark:border-table-borderDark
          bg-white dark:bg-table-bgDark
          rounded-b-table
          ">
          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <span className="whitespace-nowrap">Rows per page:</span>

            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="
                px-2 py-1
                border border-gray-300 dark:border-gray-600
                bg-white dark:bg-gray-800
                text-gray-800 dark:text-gray-200
                rounded-md
                focus:outline-none focus:ring-2 focus:ring-primary
                cursor-pointer
              "
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>

          {/* PAGE NUMBERS */}
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevPage}
              disabled={getCurrentPage() === 1}
              className="
              px-2.5 py-1
              border border-gray-300 dark:border-gray-600
              rounded-md
              bg-white dark:bg-gray-800
              text-gray-700 dark:text-gray-200
              hover:bg-gray-100 dark:hover:bg-gray-700
              disabled:opacity-40
              transition
              "
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </button>

            {generatePageNumbers().map((page, index) =>
              page < 0 ? (
                <span key={index} className="px-2">...</span>
              ) : (
                <button
                  key={`${page}-${index}`}
                  onClick={() => handlePageClick(page)}
                  className={`
                    px-3 py-1
                    rounded-md
                    text-sm
                    transition
                    ${page === getCurrentPage()
                      ? "bg-blue-500 text-white"
                      : "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }
                    `}
                >
                  {page}
                </button>
              )
            )}

            <button
              onClick={handleNextPage}
              disabled={getCurrentPage() === totalPagesSafe}
              className="px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 dark:colore-white rounded">
              <FontAwesomeIcon icon={faAngleRight} />
            </button>
          </div>
        </div>
        {/* </div> */}
        {/* )} */}
      </div>
    </>
  );
};

export default DataTable;
